import { applySuggestionPreviewToForm } from '../../features/assets/asset-suggestions.util';
import { titleFromFileName } from '../../features/assets/asset.mapper';
import type {
  AcceptSuggestionsRequestBody,
  ApplySuggestionsRequestBody,
  AssetAiAnalysisStatusApi,
  AssetAiSuggestionDecisionApi,
  AssetAiSuggestionGroupResponseDto,
  AssetAiSuggestionResponseDto,
  AssetAiSuggestionTypeApi,
  AssetDetailResponseDto,
  AssetItemAiAnalysisResponseDto,
  AssetItemResponseDto,
  AssetResponseDto,
  AssetTypeApi,
  AssetVersionDetailResponseDto,
  AssetVersionHistoryResponseDto,
  AssetVersionResponseDto,
  AssetVersionSummaryResponseDto,
  ConfidentialityLevelApi,
  CreateAssetRequestBody,
  CreateDraftVersionRequestBody,
  RejectSuggestionsRequestBody,
  UpdateAssetVersionRequestBody,
} from '../../features/assets/models/asset-api.models';
import {
  mapAssetVersionToReviewForm,
  mapReviewFormToUpdateBody,
} from '../../features/assets/asset.mapper';
import {
  findMockLibraryAssetById,
  mockHandleAssetsAssistantPost,
  mockHandleAssetsBrowseGet,
  mockHandleAssetsCatalogGet,
  mockHandleAssetsFindPost,
  mockHandleAssetsPhraseTagsPost,
} from './assets-api-mock.library.handlers';
import type { MomentumUser } from '../../shared/models/momentum-user.models';
import { momentumUserDisplayName } from '../../shared/models/momentum-user.models';
import { mockHandleProjectsGet } from './assets-api-mock.projects.handlers';
import { getAssetsMockTiming } from './assets-api-mock.timing';
import { mockIndustryDtoByName, mockIndustryDtosFromNames } from './industries-api-mock.handlers';
import { mockCatalogUserFromEmail, mockMomentumUserFromOId } from './users-api-mock.fixtures';

const MOCK_ASSET_OWNER_EMAIL = 'mock.author@zuehlke.com';
const MOCK_ASSET_OWNER_USER = mockCatalogUserFromEmail(MOCK_ASSET_OWNER_EMAIL);

function mockOwnerAuditLabel(owner: MomentumUser): string {
  return owner.email?.trim() || momentumUserDisplayName(owner);
}

function resolveMockOwnerFromOId(
  ownerOId: string | null | undefined,
  fallback: MomentumUser = MOCK_ASSET_OWNER_USER,
): MomentumUser {
  if (!ownerOId?.trim()) {
    return fallback;
  }

  return mockMomentumUserFromOId(ownerOId) ?? fallback;
}

const ASSETS_ASSISTANT_PATH = '/api/assets/assistant';

const ASSET_ID_PATH = /^\/api\/assets\/([^/]+)$/;
const VERSIONS_PATH = /^\/api\/assets\/([^/]+)\/versions$/;
const VERSION_PUT_PATH = /^\/api\/assets\/([^/]+)\/versions\/([^/]+)$/;
const FILE_ITEM_PATH = /^\/api\/assets\/([^/]+)\/versions\/([^/]+)\/items\/file$/;
const REPLACE_FILE_ITEM_PATH = /^\/api\/assets\/([^/]+)\/versions\/([^/]+)\/items\/([^/]+)\/file$/;
const PUBLISH_PATH = /^\/api\/assets\/([^/]+)\/versions\/([^/]+)\/publish$/;
const AI_ANALYSIS_PATH = /^\/api\/assets\/([^/]+)\/items\/versions\/([^/]+)\/ai-analysis$/;
const SUGGESTIONS_PATH = /^\/api\/assets\/([^/]+)\/versions\/([^/]+)\/suggestions$/;
const SUGGESTIONS_ACCEPT_PATH = /^\/api\/assets\/([^/]+)\/versions\/([^/]+)\/suggestions\/accept$/;
const SUGGESTIONS_REJECT_PATH = /^\/api\/assets\/([^/]+)\/versions\/([^/]+)\/suggestions\/reject$/;
const SUGGESTIONS_APPLY_PATH = /^\/api\/assets\/([^/]+)\/versions\/([^/]+)\/suggestions\/apply$/;
const DOWNLOAD_PATH = /^\/api\/assets\/([^/]+)\/items\/versions\/([^/]+)\/download$/;

export interface MockAssetsBinaryResponse {
  blob: Blob;
  contentType: string;
  fileName: string;
}

export interface MockAssetsRouteResult {
  status: number;
  body: unknown;
  binary?: MockAssetsBinaryResponse;
}

interface MockAssetState {
  detail: AssetDetailResponseDto;
  itemVersionId: string | null;
  aiAnalysisPollCount: number;
  aiAnalysisStatus: AssetAiAnalysisStatusApi;
  suggestions: AssetAiSuggestionResponseDto[];
  versionSummaries: AssetVersionSummaryResponseDto[];
}

const assetsById = new Map<string, MockAssetState>();

function nowIso(): string {
  return new Date().toISOString();
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function suggestionId(versionId: string, type: AssetAiSuggestionTypeApi, value: string): string {
  return `${versionId}-${type}-${value}`.toLowerCase().replace(/\s+/g, '-');
}

function buildVersionDetail(
  versionId: string,
  versionNumber: number,
  fields: Partial<AssetVersionDetailResponseDto> &
    Pick<AssetVersionDetailResponseDto, 'title' | 'type' | 'owner'>,
): AssetVersionDetailResponseDto {
  const createdAt = nowIso();
  return {
    id: versionId,
    versionNumber,
    status: 'Draft',
    title: fields.title,
    description: fields.description ?? null,
    type: fields.type,
    industries: fields.industries ?? [],
    practices: fields.practices ?? [],
    tags: fields.tags ?? [],
    owner: fields.owner,
    confidentiality: fields.confidentiality ?? 'Internal',
    reviewDate: fields.reviewDate ?? null,
    isGolden: fields.isGolden ?? false,
    createdAt,
    createdBy: mockOwnerAuditLabel(fields.owner),
    changeReason: null,
    isPublished: false,
    hasPendingAiSuggestions: false,
  };
}

function buildFileItem(
  assetId: string,
  fileName: string,
  mimeType: string,
  sizeBytes: number,
): AssetItemResponseDto {
  const itemId = newId('item');
  const itemVersionId = newId('itemver');
  const createdAt = nowIso();
  return {
    id: itemId,
    assetId,
    currentVersionId: itemVersionId,
    kind: 'File',
    createdAt,
    createdBy: MOCK_ASSET_OWNER_EMAIL,
    isDeprecated: false,
    currentVersion: {
      id: itemVersionId,
      assetItemId: itemId,
      fileName,
      mimeType,
      sizeBytes,
      ingestionStatus: 'Pending',
      aiAnalysisStatus: 'Pending',
      createdAt,
      createdBy: MOCK_ASSET_OWNER_EMAIL,
    },
  };
}

function suggestFromFileName(fileName: string): {
  title: string;
  description: string;
  type: AssetTypeApi;
  industries: string[];
  practices: string[];
  tags: string[];
  confidentiality: ConfidentialityLevelApi;
  language: string;
} {
  const lower = fileName.toLowerCase();
  if (lower.includes('rfp') || lower.includes('proposal') || lower.endsWith('.pptx')) {
    return {
      title: 'Cloud platform modernisation — client proposal',
      description:
        'Winning proposal for a regulated enterprise client pursuing cloud platform modernisation. ' +
        'The document outlines the current-state assessment, target architecture on Azure, and a phased migration ' +
        'roadmap spanning discovery, foundation landing zone, workload rationalisation, and cutover. ' +
        'It describes the proposed team structure (engagement lead, solution architect, platform engineers, ' +
        'security and compliance specialists, and change management), delivery governance, and risk mitigations ' +
        'for data residency, legacy integrations, and operational continuity. ' +
        'Commercial sections cover indicative effort, milestone-based billing, and assumptions on client ' +
        'stakeholder availability. Differentiators emphasise prior experience in transportation and ' +
        'financial-services compliance, reusable accelerators for IaC and observability, and a proven ' +
        'playbook for brownfield migrations without business disruption.',
      type: 'Proposal',
      industries: ['Transport'],
      practices: ['Digital Solutions'],
      tags: ['cloud', 'migration', 'proposal'],
      confidentiality: 'Confidential',
      language: 'en',
    };
  }
  if (lower.includes('capability') || lower.includes('overview') || lower.endsWith('.pdf')) {
    return {
      title: 'Data & AI capability overview',
      description:
        'Capability overview deck for Data & AI practice pursuits across industries. ' +
        'Covers end-to-end analytics maturity—from data platform engineering and lakehouse patterns ' +
        'to advanced analytics, ML engineering, MLOps, and generative AI with responsible-AI guardrails. ' +
        'Includes representative case studies, reference architectures, tooling landscape (Databricks, ' +
        'Azure ML, Fabric, open-source stacks), and typical engagement models from discovery workshops ' +
        'through proof-of-value to scaled delivery. ' +
        'Sections address industry-specific use cases (transportation asset optimisation, predictive ' +
        'maintenance, customer analytics), team skills matrix, certifications, and partnership ecosystem. ' +
        'Closing slides summarise packaging options, indicative timelines, and how offerings align ' +
        'with Zühlke’s broader digital solutions and cloud practices for composite proposals.',
      type: 'Offering',
      industries: ['Others'],
      practices: ['Data & AI'],
      tags: ['AI', 'capabilities', 'analytics'],
      confidentiality: 'Internal',
      language: 'en',
    };
  }
  return {
    title: titleFromFileName(fileName),
    description:
      'AI-generated summary based on document content (mock). ' +
      'This placeholder text is intentionally long to exercise the description suggestion UI: ' +
      'multi-line preview, expand/collapse, diff against current value, and apply-to-form behaviour. ' +
      'In a real response the model would synthesise purpose, audience, key topics, entities, ' +
      'confidentiality signals, and key themes extracted from the uploaded file. ' +
      'Repeat themes and paragraph breaks help validate scrolling, truncation, and layout at ' +
      'various viewport widths without relying on production LLM latency during local development.',
    type: 'Reference',
    industries: ['Others'],
    practices: ['General'],
    tags: ['reference'],
    confidentiality: 'Internal',
    language: 'en',
  };
}

function buildSuggestionsForFile(
  assetId: string,
  versionId: string,
  itemVersionId: string,
  fileName: string,
): AssetAiSuggestionResponseDto[] {
  const suggested = suggestFromFileName(fileName);
  const entries: {
    type: AssetAiSuggestionTypeApi;
    value: string;
    confidence: number;
    reason: string;
  }[] = [
    { type: 'Title', value: suggested.title, confidence: 0.92, reason: 'From document title' },
    { type: 'Summary', value: suggested.description, confidence: 0.88, reason: 'Document summary' },
    {
      type: 'AssetType',
      value: suggested.type,
      confidence: 0.85,
      reason: 'Content classification',
    },
    {
      type: 'Industry',
      value: suggested.industries[0] ?? '',
      confidence: 0.8,
      reason: 'Industry signal',
    },
    {
      type: 'Practice',
      value: suggested.practices[0] ?? '',
      confidence: 0.78,
      reason: 'Practice signal',
    },
    {
      type: 'Confidentiality',
      value: suggested.confidentiality,
      confidence: 0.75,
      reason: 'Sensitivity heuristic',
    },
    { type: 'Language', value: suggested.language, confidence: 1, reason: 'Detected language' },
  ];

  for (const tag of suggested.tags) {
    entries.push({
      type: 'Tag',
      value: tag,
      confidence: 0.82,
      reason: 'Suggested tag',
    });
  }

  return entries.map((entry) => ({
    id: suggestionId(versionId, entry.type, entry.value),
    type: entry.type,
    value: entry.value,
    confidence: entry.confidence,
    reason: entry.reason,
    decision: 'Pending' as AssetAiSuggestionDecisionApi,
    currentAssetValue: null,
    wouldChangeValue: entry.value,
  }));
}

function countByDecision(
  suggestions: AssetAiSuggestionResponseDto[],
  decision: AssetAiSuggestionDecisionApi,
): number {
  return suggestions.filter((suggestion) => suggestion.decision === decision).length;
}

function toSuggestionGroup(state: MockAssetState): AssetAiSuggestionGroupResponseDto {
  const versionId = state.detail.currentVersionId ?? state.detail.currentVersion.id;
  return {
    assetId: state.detail.id,
    versionId,
    suggestions: state.suggestions,
    pendingCount: countByDecision(state.suggestions, 'Pending'),
    acceptedCount: countByDecision(state.suggestions, 'Accepted'),
    rejectedCount: countByDecision(state.suggestions, 'Rejected'),
    appliedCount: countByDecision(state.suggestions, 'Applied'),
  };
}

function syncPendingFlag(state: MockAssetState): void {
  const hasPending = state.suggestions.some((suggestion) => suggestion.decision === 'Pending');
  state.detail = {
    ...state.detail,
    currentVersion: {
      ...state.detail.currentVersion,
      hasPendingAiSuggestions: hasPending,
    },
  };
}

function applySuggestionsToVersion(state: MockAssetState, ids: string[]): void {
  const toApply = state.suggestions.filter(
    (suggestion) =>
      ids.includes(suggestion.id) &&
      (suggestion.decision === 'Pending' || suggestion.decision === 'Accepted'),
  );

  let form = mapAssetVersionToReviewForm(state.detail.currentVersion);
  for (const suggestion of toApply) {
    form = applySuggestionPreviewToForm(form, suggestion, (name) => mockIndustryDtoByName(name).id);
  }

  const body = mapReviewFormToUpdateBody(form);
  const owner = resolveMockOwnerFromOId(body.ownerOId, state.detail.currentVersion.owner);
  state.detail = {
    ...state.detail,
    updatedAt: nowIso(),
    currentVersion: {
      ...state.detail.currentVersion,
      title: body.title,
      description: body.description,
      type: body.type,
      industries: body.industries ?? [],
      practices: body.practices ?? [],
      tags: body.tags ?? [],
      owner,
      confidentiality: body.confidentiality,
      reviewDate: body.reviewDate,
      isGolden: body.isGolden,
    },
  };

  state.suggestions = state.suggestions.map((suggestion) => {
    if (!ids.includes(suggestion.id)) {
      return suggestion;
    }
    if (suggestion.decision === 'Rejected') {
      return suggestion;
    }
    return { ...suggestion, decision: 'Applied' as AssetAiSuggestionDecisionApi };
  });
  syncPendingFlag(state);
}

function toAssetResponse(detail: AssetDetailResponseDto): AssetResponseDto {
  return {
    id: detail.id,
    currentVersionId: detail.currentVersionId,
    latestPublishedVersionId: detail.latestPublishedVersionId,
    status: detail.status,
    createdAt: detail.createdAt,
    createdBy: detail.createdBy,
    updatedAt: detail.updatedAt,
    updatedBy: detail.updatedBy,
    currentVersion: detail.currentVersion,
  };
}

function getState(assetId: string): MockAssetState | undefined {
  return assetsById.get(assetId);
}

function toVersionSummary(
  version: AssetVersionDetailResponseDto,
  isLatest: boolean,
): AssetVersionSummaryResponseDto {
  return {
    id: version.id,
    versionNumber: version.versionNumber,
    status: version.status,
    title: version.title,
    createdAt: version.createdAt,
    createdBy: version.createdBy,
    changeReason: version.changeReason ?? null,
    isLatest,
    isPublished: version.isPublished,
  };
}

function seedMockAssetFromLibrary(
  asset: NonNullable<ReturnType<typeof findMockLibraryAssetById>>,
): void {
  if (assetsById.has(asset.assetId)) {
    return;
  }

  const assetId = asset.assetId;
  const versionId = asset.assetVersionId;
  const createdAt = new Date(Date.now() - 86400000 * 14).toISOString();
  const owner = asset.owner ?? MOCK_ASSET_OWNER_USER;
  const version = buildVersionDetail(versionId, 1, {
    title: asset.title,
    description: asset.summary,
    type: asset.type,
    industries: mockIndustryDtosFromNames(asset.industries ?? []),
    practices: [...(asset.practices ?? [])],
    tags: [...asset.tags],
    owner,
    confidentiality: asset.confidentiality,
    isGolden: false,
  });

  if (asset.isPublished) {
    version.status = 'Published';
    version.isPublished = true;
    version.publishedAt = createdAt;
    version.publishedBy = mockOwnerAuditLabel(owner);
  }

  const fileName =
    version.type === 'Reference'
      ? `${asset.title.slice(0, 40).replace(/\s+/g, '-').toLowerCase()}.pptx`
      : `${asset.title.slice(0, 40).replace(/\s+/g, '-').toLowerCase()}.pdf`;
  const mimeType =
    version.type === 'Reference'
      ? 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      : 'application/pdf';
  const item = buildFileItem(assetId, fileName, mimeType, 245_000);
  if (item.currentVersion) {
    item.currentVersion.ingestionStatus = 'Indexed';
    item.currentVersion.aiAnalysisStatus = 'Completed';
  }

  const detail: AssetDetailResponseDto = {
    id: assetId,
    currentVersionId: versionId,
    latestPublishedVersionId: asset.isPublished ? versionId : null,
    status: asset.isPublished ? 'Published' : 'Draft',
    createdAt,
    createdBy: mockOwnerAuditLabel(owner),
    updatedAt: createdAt,
    updatedBy: mockOwnerAuditLabel(owner),
    currentVersion: version,
    latestPublishedVersion: asset.isPublished ? { ...version } : null,
    items: [item],
    syncHistory:
      version.type === 'Reference'
        ? [{ occurredAtUtc: createdAt, reason: 'Created from Templafy sync' }]
        : [],
    successStoryLanguages: version.type === 'Reference' ? ['en', 'de'] : [],
  };

  assetsById.set(assetId, {
    detail,
    itemVersionId: item.currentVersionId ?? item.currentVersion?.id ?? null,
    aiAnalysisPollCount: 999,
    aiAnalysisStatus: 'Completed',
    suggestions: [],
    versionSummaries: [toVersionSummary(version, true)],
  });
}

function ensureMockAssetState(assetId: string): MockAssetState | undefined {
  const existing = getState(assetId);
  if (existing) {
    return existing;
  }

  const libraryAsset = findMockLibraryAssetById(assetId);
  if (!libraryAsset) {
    return undefined;
  }

  seedMockAssetFromLibrary(libraryAsset);
  return getState(assetId);
}

export function mockHandleAssetsCreatePost(body: unknown): {
  status: number;
  body: AssetResponseDto;
} {
  const request = body as CreateAssetRequestBody;
  const assetId = newId('asset');
  const versionId = newId('ver');
  const createdAt = nowIso();
  const owner = MOCK_ASSET_OWNER_USER;
  const version = buildVersionDetail(versionId, 1, {
    title: request.title,
    description: request.description,
    type: request.type ?? 'Other',
    industries: request.industries ?? [],
    practices: request.practices ?? [],
    tags: request.tags ?? [],
    owner,
    confidentiality: request.confidentiality ?? 'Internal',
    reviewDate: request.reviewDate,
    isGolden: request.isGolden ?? false,
  });

  const detail: AssetDetailResponseDto = {
    id: assetId,
    currentVersionId: versionId,
    latestPublishedVersionId: null,
    status: 'Draft',
    createdAt,
    createdBy: mockOwnerAuditLabel(owner),
    updatedAt: createdAt,
    updatedBy: mockOwnerAuditLabel(owner),
    currentVersion: version,
    latestPublishedVersion: null,
    items: [],
  };

  assetsById.set(assetId, {
    detail,
    itemVersionId: null,
    aiAnalysisPollCount: 0,
    aiAnalysisStatus: 'NotStarted',
    suggestions: [],
    versionSummaries: [toVersionSummary(version, true)],
  });

  return { status: 201, body: toAssetResponse(detail) };
}

export function mockHandleAssetsGet(assetId: string): {
  status: number;
  body: AssetDetailResponseDto | null;
} {
  const state = ensureMockAssetState(assetId);
  if (!state) {
    return { status: 404, body: null };
  }
  return { status: 200, body: state.detail };
}

export function mockHandleAssetsVersionHistoryGet(assetId: string): {
  status: number;
  body: AssetVersionHistoryResponseDto | null;
} {
  const state = ensureMockAssetState(assetId);
  if (!state) {
    return { status: 404, body: null };
  }

  return {
    status: 200,
    body: {
      assetId,
      versions: [...state.versionSummaries].sort(
        (left, right) => right.versionNumber - left.versionNumber,
      ),
    },
  };
}

export function mockHandleAssetsCreateDraftVersionPost(
  assetId: string,
  body: unknown,
): { status: number; body: AssetVersionResponseDto | null } {
  const state = ensureMockAssetState(assetId);
  if (!state) {
    return { status: 404, body: null };
  }

  if (state.detail.currentVersion.status === 'Draft') {
    return { status: 400, body: null };
  }

  const request = body as CreateDraftVersionRequestBody;
  const source =
    state.detail.currentVersion.status === 'Published'
      ? state.detail.currentVersion
      : (state.detail.latestPublishedVersion ?? state.detail.currentVersion);

  const nextVersionNumber =
    state.versionSummaries.reduce((max, row) => Math.max(max, row.versionNumber), 0) + 1;
  const versionId = newId('ver');
  const createdAt = nowIso();
  const owner = resolveMockOwnerFromOId(request.ownerOId, source.owner);

  const draft = buildVersionDetail(versionId, nextVersionNumber, {
    title: request.title ?? source.title,
    description: request.description ?? source.description,
    type: request.type ?? source.type,
    industries: request.industries ?? source.industries,
    practices: request.practices ?? source.practices,
    tags: request.tags ?? source.tags,
    owner,
    confidentiality: request.confidentiality ?? source.confidentiality,
    reviewDate: request.reviewDate ?? source.reviewDate,
    isGolden: request.isGolden ?? source.isGolden,
  });
  draft.changeReason = request.changeReason?.trim() || null;
  draft.createdBy = mockOwnerAuditLabel(owner);

  const publishedSnapshot =
    state.detail.latestPublishedVersion ?? (source.isPublished ? { ...source } : null);

  state.versionSummaries = state.versionSummaries.map((row) => ({
    ...row,
    isLatest: false,
  }));
  state.versionSummaries.push(toVersionSummary(draft, true));

  state.detail = {
    ...state.detail,
    status: publishedSnapshot ? 'Published' : 'Draft',
    currentVersionId: versionId,
    updatedAt: createdAt,
    updatedBy: mockOwnerAuditLabel(owner),
    currentVersion: draft,
    latestPublishedVersion: publishedSnapshot,
    items: [...state.detail.items],
  };
  state.aiAnalysisPollCount = 0;
  state.aiAnalysisStatus = 'NotStarted';
  state.suggestions = [];

  return {
    status: 201,
    body: {
      id: draft.id,
      assetId,
      versionNumber: draft.versionNumber,
      status: draft.status,
      title: draft.title,
      description: draft.description,
      type: draft.type,
      industries: draft.industries ?? [],
      practices: draft.practices ?? [],
      tags: draft.tags,
      owner: draft.owner,
      confidentiality: draft.confidentiality,
      reviewDate: draft.reviewDate,
      isGolden: draft.isGolden,
      createdAt: draft.createdAt,
      createdBy: draft.createdBy,
      changeReason: draft.changeReason,
      isLatest: true,
      isPublished: false,
      hasPendingAiSuggestions: false,
      publishedAt: null,
      publishedBy: null,
    },
  };
}

export function mockHandleAssetsFileUploadPost(
  canonical: string,
  body: unknown,
): { status: number; body: AssetItemResponseDto | null } {
  const replaceMatch = REPLACE_FILE_ITEM_PATH.exec(canonical);
  const addMatch = FILE_ITEM_PATH.exec(canonical);
  if (!replaceMatch && !addMatch) {
    return { status: 404, body: null };
  }

  const assetId = replaceMatch?.[1] ?? addMatch?.[1] ?? '';
  const versionId = replaceMatch?.[2] ?? addMatch?.[2] ?? '';
  const replaceItemId = replaceMatch?.[3];
  const state = ensureMockAssetState(assetId);
  if (!state || state.detail.currentVersionId !== versionId) {
    return { status: 404, body: null };
  }

  let fileName = titleFromFileName(state.detail.currentVersion.title);
  let mimeType = 'application/octet-stream';
  let sizeBytes = 0;

  if (body instanceof FormData) {
    const entry = body.get('files');
    if (entry instanceof File) {
      fileName = entry.name;
      mimeType = entry.type || mimeType;
      sizeBytes = entry.size;
    }
  }

  let item: AssetItemResponseDto;
  let items = [...state.detail.items];
  if (replaceItemId) {
    const existingIndex = items.findIndex((row) => row.id === replaceItemId && !row.isDeprecated);
    if (existingIndex < 0) {
      return { status: 404, body: null };
    }

    const existing = items[existingIndex];
    const itemVersionId = newId('itemver');
    const createdAt = nowIso();
    item = {
      ...existing,
      currentVersionId: itemVersionId,
      currentVersion: {
        id: itemVersionId,
        assetItemId: existing.id,
        fileName,
        mimeType,
        sizeBytes,
        ingestionStatus: 'Pending',
        aiAnalysisStatus: 'Pending',
        createdAt,
        createdBy: MOCK_ASSET_OWNER_EMAIL,
      },
    };
    items[existingIndex] = item;
  } else {
    item = buildFileItem(assetId, fileName, mimeType, sizeBytes);
    items = [...items, item];
  }

  state.itemVersionId = item.currentVersionId ?? item.currentVersion?.id ?? null;
  state.aiAnalysisPollCount = 0;
  state.aiAnalysisStatus = 'Processing';
  state.suggestions = [];
  state.detail = {
    ...state.detail,
    updatedAt: nowIso(),
    items,
    currentVersion: {
      ...state.detail.currentVersion,
      hasPendingAiSuggestions: false,
    },
  };

  return { status: 201, body: item };
}

export function mockHandleAssetsAiAnalysisGet(
  assetId: string,
  itemVersionId: string,
): { status: number; body: AssetItemAiAnalysisResponseDto | null } {
  const state = ensureMockAssetState(assetId);
  if (!state || state.itemVersionId !== itemVersionId) {
    return { status: 404, body: null };
  }

  const { analysisReadyAfterGetPolls } = getAssetsMockTiming();
  if (state.aiAnalysisStatus === 'Processing') {
    state.aiAnalysisPollCount += 1;
    if (state.aiAnalysisPollCount >= analysisReadyAfterGetPolls) {
      state.aiAnalysisStatus = 'Completed';
      const fileName =
        state.detail.items[0]?.currentVersion?.fileName ??
        titleFromFileName(state.detail.currentVersion.title);
      state.suggestions = buildSuggestionsForFile(
        assetId,
        state.detail.currentVersionId ?? state.detail.currentVersion.id,
        itemVersionId,
        fileName,
      );
      syncPendingFlag(state);
      if (state.detail.items[0]?.currentVersion) {
        state.detail.items[0].currentVersion.aiAnalysisStatus = 'Completed';
      }
    }
  }

  return {
    status: 200,
    body: {
      assetItemVersionId: itemVersionId,
      ingestionStatus: state.detail.items[0]?.currentVersion?.ingestionStatus ?? 'Pending',
      aiAnalysisStatus: state.aiAnalysisStatus,
      summary: null,
      detectedContentLanguage: null,
      error: state.aiAnalysisStatus === 'Failed' ? 'Mock analysis failed' : null,
    },
  };
}

export function mockHandleAssetsSuggestionsGet(
  assetId: string,
  versionId: string,
): { status: number; body: AssetAiSuggestionGroupResponseDto | null } {
  const state = ensureMockAssetState(assetId);
  if (!state || state.detail.currentVersionId !== versionId) {
    return { status: 404, body: null };
  }
  return { status: 200, body: toSuggestionGroup(state) };
}

function updateSuggestionDecisions(
  state: MockAssetState,
  ids: string[],
  decision: AssetAiSuggestionDecisionApi,
): AssetAiSuggestionGroupResponseDto {
  state.suggestions = state.suggestions.map((suggestion) => {
    if (!ids.includes(suggestion.id) || suggestion.decision !== 'Pending') {
      return suggestion;
    }
    return { ...suggestion, decision };
  });
  syncPendingFlag(state);
  return toSuggestionGroup(state);
}

export function mockHandleAssetsSuggestionsAcceptPost(
  assetId: string,
  versionId: string,
  body: unknown,
): { status: number; body: AssetAiSuggestionGroupResponseDto | null } {
  const state = ensureMockAssetState(assetId);
  if (!state || state.detail.currentVersionId !== versionId) {
    return { status: 404, body: null };
  }
  const request = body as AcceptSuggestionsRequestBody;
  return {
    status: 200,
    body: updateSuggestionDecisions(state, request.suggestionIds ?? [], 'Accepted'),
  };
}

export function mockHandleAssetsSuggestionsRejectPost(
  assetId: string,
  versionId: string,
  body: unknown,
): { status: number; body: AssetAiSuggestionGroupResponseDto | null } {
  const state = ensureMockAssetState(assetId);
  if (!state || state.detail.currentVersionId !== versionId) {
    return { status: 404, body: null };
  }
  const request = body as RejectSuggestionsRequestBody;
  return {
    status: 200,
    body: updateSuggestionDecisions(state, request.suggestionIds ?? [], 'Rejected'),
  };
}

export function mockHandleAssetsSuggestionsApplyPost(
  assetId: string,
  versionId: string,
  body: unknown,
): { status: number; body: AssetAiSuggestionGroupResponseDto | null } {
  const state = ensureMockAssetState(assetId);
  if (!state || state.detail.currentVersionId !== versionId) {
    return { status: 404, body: null };
  }
  const request = body as ApplySuggestionsRequestBody;
  applySuggestionsToVersion(state, request.suggestionIds ?? []);
  return { status: 200, body: toSuggestionGroup(state) };
}

export function mockHandleAssetsVersionPut(
  canonical: string,
  body: unknown,
): { status: number; body: AssetDetailResponseDto | null } {
  const match = VERSION_PUT_PATH.exec(canonical);
  if (!match) {
    return { status: 404, body: null };
  }

  const [, assetId, versionId] = match;
  const state = ensureMockAssetState(assetId);
  if (!state || state.detail.currentVersionId !== versionId) {
    return { status: 404, body: null };
  }

  if (state.detail.currentVersion.status !== 'Draft') {
    return { status: 400, body: null };
  }

  const request = body as UpdateAssetVersionRequestBody;
  const version = state.detail.currentVersion;
  const owner = resolveMockOwnerFromOId(request.ownerOId, version.owner);
  state.detail = {
    ...state.detail,
    updatedAt: nowIso(),
    updatedBy: mockOwnerAuditLabel(owner),
    currentVersion: {
      ...version,
      title: request.title,
      description: request.description ?? null,
      type: request.type,
      industries: request.industries ?? [],
      practices: request.practices ?? [],
      tags: request.tags ?? [],
      owner,
      confidentiality: request.confidentiality,
      reviewDate: request.reviewDate ?? null,
      isGolden: request.isGolden,
      changeReason:
        request.changeReason !== undefined
          ? request.changeReason?.trim() || null
          : version.changeReason,
      ...(request.projectCNumbers != null
        ? {
            projects: request.projectCNumbers.map((cNumber) => ({
              cNumber,
              title: cNumber,
              status: 'Linked' as const,
            })),
          }
        : {}),
    },
  };

  return { status: 200, body: state.detail };
}

export function mockHandleAssetsPublishPost(canonical: string): {
  status: number;
  body: AssetResponseDto | null;
} {
  const match = PUBLISH_PATH.exec(canonical);
  if (!match) {
    return { status: 404, body: null };
  }

  const [, assetId, versionId] = match;
  const state = ensureMockAssetState(assetId);
  if (!state || state.detail.currentVersionId !== versionId) {
    return { status: 404, body: null };
  }

  const publishedAt = nowIso();
  const version: AssetVersionDetailResponseDto = {
    ...state.detail.currentVersion,
    status: 'Published',
    isPublished: true,
    publishedAt,
    publishedBy: state.detail.updatedBy,
    hasPendingAiSuggestions: false,
  };

  state.versionSummaries = state.versionSummaries.map((row) =>
    row.id === versionId
      ? toVersionSummary(version, true)
      : row.isPublished
        ? { ...row, isLatest: false, status: 'Superseded' }
        : { ...row, isLatest: false },
  );

  state.detail = {
    ...state.detail,
    status: 'Published',
    latestPublishedVersionId: versionId,
    updatedAt: publishedAt,
    currentVersion: version,
    latestPublishedVersion: null,
  };

  if (state.detail.items[0]?.currentVersion) {
    state.detail.items[0].currentVersion.ingestionStatus = 'Indexed';
  }

  return { status: 200, body: toAssetResponse(state.detail) };
}

function mockHandleAssetsDownloadGet(
  assetId: string,
  itemVersionId: string,
  requestUrl: string | undefined,
): MockAssetsRouteResult {
  const state = ensureMockAssetState(assetId);
  if (!state) {
    return { status: 404, body: null };
  }

  const item = state.detail.items.find(
    (row) => row.currentVersion?.id === itemVersionId && !row.isDeprecated,
  );
  const version = item?.currentVersion;
  if (!version?.fileName) {
    return { status: 404, body: null };
  }

  let lang: string | null = null;
  if (requestUrl) {
    try {
      lang = new URL(requestUrl, 'http://localhost').searchParams.get('lang');
    } catch {
      lang = null;
    }
  }

  const mimeType = version.mimeType ?? 'application/octet-stream';
  const fileName =
    lang?.toLowerCase() === 'de'
      ? version.fileName.replace(/(\.[^.]+)?$/, '-de$1')
      : version.fileName;
  const label = lang?.toLowerCase() === 'de' ? 'Mock German deck' : 'Mock file content';

  return {
    status: 200,
    body: null,
    binary: {
      blob: new Blob([label], { type: mimeType }),
      contentType: mimeType,
      fileName,
    },
  };
}

export function mockHandleAssetsRoute(
  method: string,
  canonical: string,
  body: unknown,
  requestUrl?: string,
): MockAssetsRouteResult | null {
  if (method === 'GET' && canonical === '/api/assets/catalog') {
    return mockHandleAssetsCatalogGet(requestUrl ?? canonical);
  }

  if (method === 'GET' && canonical === '/api/assets/browse') {
    return mockHandleAssetsBrowseGet(requestUrl ?? canonical);
  }

  if (method === 'POST' && canonical === '/api/assets/browse/phrase-tags') {
    return mockHandleAssetsPhraseTagsPost(body);
  }

  if (method === 'POST' && canonical === '/api/assets/find') {
    return mockHandleAssetsFindPost(body);
  }

  if (method === 'POST' && canonical === ASSETS_ASSISTANT_PATH) {
    return mockHandleAssetsAssistantPost(body);
  }

  if (method === 'POST' && canonical === '/api/assets') {
    return mockHandleAssetsCreatePost(body);
  }

  if (method === 'GET' && canonical.startsWith('/api/projects')) {
    return mockHandleProjectsGet(requestUrl ?? canonical);
  }

  const assetGet = ASSET_ID_PATH.exec(canonical);
  if (method === 'GET' && assetGet) {
    return mockHandleAssetsGet(assetGet[1]);
  }

  const versionsPath = VERSIONS_PATH.exec(canonical);
  if (versionsPath) {
    if (method === 'GET') {
      return mockHandleAssetsVersionHistoryGet(versionsPath[1]);
    }
    if (method === 'POST') {
      return mockHandleAssetsCreateDraftVersionPost(versionsPath[1], body);
    }
  }

  if (
    method === 'POST' &&
    (FILE_ITEM_PATH.test(canonical) || REPLACE_FILE_ITEM_PATH.test(canonical))
  ) {
    return mockHandleAssetsFileUploadPost(canonical, body);
  }

  const aiAnalysis = AI_ANALYSIS_PATH.exec(canonical);
  if (method === 'GET' && aiAnalysis) {
    return mockHandleAssetsAiAnalysisGet(aiAnalysis[1], aiAnalysis[2]);
  }

  const suggestionsGet = SUGGESTIONS_PATH.exec(canonical);
  if (method === 'GET' && suggestionsGet) {
    return mockHandleAssetsSuggestionsGet(suggestionsGet[1], suggestionsGet[2]);
  }

  const accept = SUGGESTIONS_ACCEPT_PATH.exec(canonical);
  if (method === 'POST' && accept) {
    return mockHandleAssetsSuggestionsAcceptPost(accept[1], accept[2], body);
  }

  const reject = SUGGESTIONS_REJECT_PATH.exec(canonical);
  if (method === 'POST' && reject) {
    return mockHandleAssetsSuggestionsRejectPost(reject[1], reject[2], body);
  }

  const apply = SUGGESTIONS_APPLY_PATH.exec(canonical);
  if (method === 'POST' && apply) {
    return mockHandleAssetsSuggestionsApplyPost(apply[1], apply[2], body);
  }

  if (method === 'PUT' && VERSION_PUT_PATH.test(canonical)) {
    return mockHandleAssetsVersionPut(canonical, body);
  }

  if (method === 'POST' && PUBLISH_PATH.test(canonical)) {
    return mockHandleAssetsPublishPost(canonical);
  }

  const download = DOWNLOAD_PATH.exec(canonical);
  if (method === 'GET' && download) {
    return mockHandleAssetsDownloadGet(download[1], download[2], requestUrl);
  }

  return null;
}
